import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface LocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  initialCity?: string | null;
  initialCountry?: string | null;
}

export const LocationDialog = ({
 
  open, 
  onOpenChange, 
  userId, 
  initialCity, 
  initialCountry 
}: LocationDialogProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [city, setCity] = useState(initialCity || "");
  const [country, setCountry] = useState(initialCountry || "");

  // Update local state when initial values change
  useEffect(() => {
    setCity(initialCity || "");
    setCountry(initialCountry || "");
  }, [initialCity, initialCountry]);

  const updateLocation = useMutation({
    mutationFn: async ({ city, country }: { city: string; country: string }) => {
      const { error } = await supabase
        .from("profiles")
        .update({ city, country })
        .eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["profile-settings"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      toast.success(t('notifications.toast.location_updated'));
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t('notifications.toast.failed_to_update_location'));
    },
  });

  const handleSave = () => {
    if (!city || !country) {
      toast.error(t('notifications.toast.please_fill_in_both_city_and_country'));
      return;
    }
    updateLocation.mutate({ city, country });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('social.location_dialog.title')}</DialogTitle>
          <DialogDescription>{t('social.location_dialog.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="city">{t('social.location_dialog.city_label')}</Label>
            <Input
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder={t('social.location_dialog.city_placeholder')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="country">{t('social.location_dialog.country_label')}</Label>
            <Input
              id="country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder={t('social.location_dialog.country_placeholder')}
            />
          </div>
          <Button 
            onClick={handleSave}
            disabled={updateLocation.isPending || !city || !country}
            className="w-full"
          >
            <MapPin className="w-4 h-4 mr-2" />
            {updateLocation.isPending ? t('social.location_dialog.saving') : t('social.location_dialog.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
